import PropertyCard from "@/atoms/PropertyCard";
import PropertyCardSkeleton from "@/atoms/PropertyCardSkeleton";
import React, { Fragment } from "react";
import DashboardButton from "@/atoms/DashboardButton";
import { Listing } from "@/services/apiTypes";
import GeneralPropertyCard from "@/components/properties/GeneralPropertyCard";
import NeighbourHoodIntrestCard from "@/components/properties/NeighbourHoodIntrestCard";
import { Development } from "@/services/cms/apiTypes";
import getConfig from "next/config";
const { publicRuntimeConfig } = getConfig();

export type PropertiesListProps = {
  listings?: Listing[];
  hasNextPage: boolean;
  isLoadingListing?: boolean;
  searchTerm?: string;
  handleLoadMoreProperties: () => void;
  searchRadius: number;
  totalListings?: number;
  oneDevelopment?: Development;
};
const PropertiesList = ({
  listings,
  hasNextPage,
  searchTerm,
  handleLoadMoreProperties,
  isLoadingListing,
  searchRadius,
  oneDevelopment,
}: PropertiesListProps) => {
  const propertiesWithinRadius =
    searchRadius === 0
      ? listings
      : listings?.filter((property) => (property.distance_from_search || 0) < searchRadius);
  const propertiesOutsideRadius =
    searchRadius === 0
      ? []
      : listings?.filter((property) => (property.distance_from_search || 0) > searchRadius);

  // don't show the neighbourhood interest card if the development is in the same neighbourhood as the search term
  const developmentInSameNeighbourhood =
    oneDevelopment?.attributes?.neighbourhood?.data?.attributes?.slug === searchTerm?.toLowerCase();

  // Show skeleton loading while data is loading
  if (isLoadingListing && (!listings || listings.length === 0)) {
    return (
      <>
        {searchTerm && (
          <section className="flex flex-col items-center justify-center w-auto mt-9 max-w-[90rem] mb-5 md:mb-12 mx-auto lg:px-[1.5rem]">
            <h2 className=" text-[24px] leading-[26px] md:text-[32px] xl:text-4xl font-bold text-center">{`Keyzy properties available in ${searchTerm
              .charAt(0)
              .toUpperCase()}${searchTerm.slice(1)}`}</h2>
          </section>
        )}
        <section
          className="flex flex-wrap items-center justify-left w-auto mt-9 max-w-[90rem] mb-5 md:mb-12 mx-auto lg:px-[1.5rem]"
          role="tablist"
        >
          {/* Show 6 skeleton cards while loading */}
          {Array.from({ length: 6 }).map((_, idx) => (
            <Fragment key={`skeleton-${idx}`}>
              <div className={`flex flex-col w-full md:w-1/2 lg:w-1/3 mb-8 md:px-4`}>
                <PropertyCardSkeleton />
              </div>
            </Fragment>
          ))}
        </section>
      </>
    );
  }

  return (
    <>
      {listings && listings?.length > 0 && (
        <>
          {propertiesWithinRadius && propertiesWithinRadius?.length > 0 ? (
            <>
              {searchTerm && (
                <section className="flex flex-col items-center justify-center w-auto mt-9 max-w-[90rem]  md:mb-12 mx-auto lg:px-[1.5rem] ">
                  <h2 className=" text-[24px] leading-[26px] md:text-[32px] xl:text-4xl font-bold text-center">{`Keyzy properties available in ${searchTerm
                    .charAt(0)
                    .toUpperCase()}${searchTerm.slice(1)}`}</h2>
                </section>
              )}
              <section
                className="flex flex-wrap items-stretch justify-left w-auto mt-5 md:mt-9 max-w-[90rem] mb-5 md:mb-12 mx-auto lg:pl-[5px] lg:pr-[4px]"
                role="tablist"
              >
                {propertiesWithinRadius?.map((property, idx) => (
                  <>
                    {propertiesWithinRadius.length > 3 &&
                      idx === 3 &&
                      !developmentInSameNeighbourhood &&
                      oneDevelopment && (
                        <Fragment key={idx + 20}>
                          <div className={`flex flex-col w-full md:w-1/2 lg:w-1/3 mb-8 md:px-4`}>
                            <NeighbourHoodIntrestCard development={oneDevelopment} />
                          </div>
                        </Fragment>
                      )}
                    <Fragment key={idx}>
                      <div className={`flex flex-col w-full md:w-1/2 lg:w-1/3 mb-8 md:px-4`}>
                        <PropertyCard property={property} searchTerm={searchTerm} />
                      </div>
                    </Fragment>
                  </>
                ))}
                {propertiesWithinRadius?.length < 3 &&
                  !developmentInSameNeighbourhood &&
                  oneDevelopment && (
                    <Fragment>
                      <div className={`flex flex-col w-full md:w-1/2 lg:w-1/3 mb-8 md:px-4 `}>
                        <NeighbourHoodIntrestCard development={oneDevelopment} />
                      </div>
                    </Fragment>
                  )}
                <Fragment>
                  <div className={`flex flex-col w-full md:w-1/2 lg:w-1/3 mb-8 md:px-4 `}>
                    <GeneralPropertyCard />
                  </div>
                </Fragment>
              </section>
            </>
          ) : (
            <section className="flex flex-col items-center justify-center w-auto mt-9 max-w-[90rem] mb-5  mx-auto lg:px-[1.5rem]">
              <div className="text-[32px] font-bold">{`Sorry, looks like we don't yet have anything in ${searchTerm}...`}</div>
            </section>
          )}
          {propertiesOutsideRadius && propertiesOutsideRadius?.length > 0 && (
            <>
              <section className="flex flex-col items-center justify-center w-auto mt-9 max-w-[90rem] mb-5 md:mb-12 mx-auto lg:px-[1.5rem]">
                <div className="text-[32px] font-bold ">Other homes available elsewhere...</div>
              </section>
              <section
                className="flex flex-wrap items-center justify-left w-auto mt-9 max-w-[90rem] mb-5 md:mb-12 mx-auto lg:px-[1.5rem]"
                role="tablist"
              >
                {propertiesOutsideRadius?.map((property, idx) => (
                  <Fragment key={idx}>
                    <div className={`flex flex-col w-full md:w-1/2 lg:w-1/3 mb-8 md:px-4`}>
                      <PropertyCard property={property} />
                    </div>
                  </Fragment>
                ))}
              </section>
            </>
          )}
          {hasNextPage && (
            <div
              className="flex justify-center mb-5 w-full md:w-auto items-center"
              onClick={() => handleLoadMoreProperties()}
            >
              <DashboardButton
                className="px-4 py-2 w-full md:w-auto rounded-sm md:rounded-lg"
                variant="submit"
                disabled={isLoadingListing}
              >
                {isLoadingListing ? "Loading..." : "Load more properties"}
              </DashboardButton>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default PropertiesList;
